import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved,
  faSpinner,
  faCheck,
  faXmark,
  faRotateLeft,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faArrowRotateRight,
  faChevronDown,
  faChevronUp,
  faHandPointer,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from '@/lib/swal';
import { useAuthStore } from '@/stores/auth.store';
import { approvalRequestTypeService } from '@/services/approval-request-type.service';
import {
  authorizationCaseService,
  type EvidenceSelectionItem,
} from '@/services/authorization-case.service';
import { EvidenceSelectionPicker } from '@/features/authorization/components/EvidenceSelectionPicker';
import {
  useStepAuthorization,
  useRequestStepAuthorization,
  useRespondStepAuthorization,
  useResubmitStepAuthorization,
} from '../../hooks/useStepAuthorization';

function hasPermission(permissions: string[], perm: string): boolean {
  return permissions.includes('*') || permissions.includes(perm);
}

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtDateTime(v: string): string {
  return new Date(v).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const STAGE_LABEL: Record<string, string> = {
  PENDING_OPS_REVIEW: 'Pending OPS Review',
  RETURNED_TO_ENGINEER: 'Returned to Engineer',
  FORWARDED_TO_CUSTOMER: 'Forwarded to Customer',
  CUSTOMER_PENDING: 'Customer Pending',
  RESOLVED: 'Resolved',
};

const ACTION_LABEL: Record<string, string> = {
  REQUESTED: 'Requested',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RETURNED: 'Returned to Engineer',
  COMMENTED: 'Commented',
};

interface Props {
  inspectionId: string;
  stepExecutionId: string;
  authorizationRequired: boolean;
  authorization: { id: string; status: string } | null;
  canExecute: boolean;
  isExpanded: boolean;
}

type TypeMode = 'catalog' | 'custom';

interface RequestFormState {
  reason: string;
  typeMode: TypeMode;
  selectedTypeId: string;
  customTypeName: string;
  evidence: EvidenceSelectionItem[];
}

function initialRequestForm(): RequestFormState {
  return { reason: '', typeMode: 'catalog', selectedTypeId: '', customTypeName: '', evidence: [] };
}

function TypeSelector({
  typeMode,
  selectedTypeId,
  customTypeName,
  approvalTypes,
  onChange,
  disabled,
}: {
  typeMode: TypeMode;
  selectedTypeId: string;
  customTypeName: string;
  approvalTypes: { id: string; name: string }[];
  onChange: (patch: Partial<RequestFormState>) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <div>
        <span className="text-xs font-medium text-foreground">
          Approval Type <span className="text-destructive">*</span>
        </span>
        <div className="mt-1.5 flex gap-2">
          {(['catalog', 'custom'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              disabled={disabled}
              onClick={() => onChange({ typeMode: mode, selectedTypeId: '', customTypeName: '' })}
              className={`rounded-md border px-3 py-1 text-xs font-medium transition disabled:opacity-50 ${typeMode === mode ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground hover:border-foreground/40'}`}
            >
              {mode === 'catalog' ? 'Select from list' : 'Other / Custom'}
            </button>
          ))}
        </div>
      </div>

      {typeMode === 'catalog' && (
        <select
          value={selectedTypeId}
          onChange={(e) => onChange({ selectedTypeId: e.target.value })}
          disabled={disabled}
          className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        >
          <option value="">— Select type —</option>
          {approvalTypes.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      )}

      {typeMode === 'custom' && (
        <input
          type="text"
          value={customTypeName}
          onChange={(e) => onChange({ customTypeName: e.target.value })}
          maxLength={200}
          disabled={disabled}
          placeholder="e.g. OEM Engineering Review"
          className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
        />
      )}
    </div>
  );
}

export function StepAuthorizationSection({
  inspectionId,
  stepExecutionId,
  authorizationRequired,
  authorization,
  canExecute,
  isExpanded,
}: Props) {
  const user = useAuthStore((s) => s.user);
  const permissions = user?.permissions ?? [];
  const hasRespondPermission = hasPermission(permissions, 'inspection:authorization:respond');
  const canResubmit = hasPermission(permissions, 'inspection:authorization:resubmit');

  const {
    data: fullAuth,
    isLoading,
    refetch: refetchAuthorization,
  } = useStepAuthorization(
    inspectionId,
    stepExecutionId,
    isExpanded && authorizationRequired && authorization !== null,
  );

  const requestMutation = useRequestStepAuthorization(inspectionId);
  const respondMutation = useRespondStepAuthorization(inspectionId);
  const resubmitMutation = useResubmitStepAuthorization(inspectionId);

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState<RequestFormState>(initialRequestForm());
  const [respondComment, setRespondComment] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [resubmitReason, setResubmitReason] = useState('');
  const [showResubmitForm, setShowResubmitForm] = useState(false);

  const patchRequest = (patch: Partial<RequestFormState>) =>
    setRequestForm((prev) => ({ ...prev, ...patch }));

  const { data: approvalTypes } = useQuery({
    queryKey: ['approval-request-types-all'],
    queryFn: () => approvalRequestTypeService.listAllActive(),
    staleTime: 60_000,
    enabled: showRequestForm,
  });

  if (!authorizationRequired) return null;

  /**
   * ACTION GATING — mirrors `respondForStep`'s server guard exactly, and
   * `AuthorizationCaseDetailPage`'s gating for the same case in the
   * Authorization Center. The server stays authoritative; this only stops
   * offering a control the backend will refuse.
   *
   * Before this, the buttons were shown on `inspection:authorization:respond`
   * alone — a permission Commercial and Admin also hold — so a Commercial user
   * was invited to approve an OPS-owned case, which the unguarded endpoint then
   * carried out, resolving it straight to the engineer.
   */
  const actorOwnerClass =
    user?.roleSlug === 'operations-user'
      ? 'OPS'
      : user?.roleSlug === 'commercial-user'
        ? 'COMMERCIAL'
        : user?.roleSlug === 'admin'
          ? 'ADMIN'
          : null;
  const isOwnerRole = user?.roleSlug === 'owner';
  const isCurrentOwner = actorOwnerClass !== null && actorOwnerClass === fullAuth?.currentOwner;
  // Only OPS and COMMERCIAL use private per-user assignment. ADMIN does not, and
  // must not be made to claim before acting.
  const ownerUsesAssignment =
    fullAuth?.currentOwner === 'OPS' || fullAuth?.currentOwner === 'COMMERCIAL';
  const isUnclaimed = !fullAuth?.assignedUserId;
  const isAssignee = !!fullAuth?.assignedUserId && fullAuth.assignedUserId === user?.userId;

  const mayAct = isOwnerRole || (isCurrentOwner && (ownerUsesAssignment ? isAssignee : true));
  const canRespond = hasRespondPermission && mayAct;
  /** Claim takes an unclaimed case out of the department intake pool. */
  const canClaim = hasRespondPermission && isCurrentOwner && ownerUsesAssignment && isUnclaimed;
  /** Only OPS ever returns a case to the engineer (design §5). */
  const canReturnToEngineer = canRespond && fullAuth?.currentOwner === 'OPS';

  async function handleClaim() {
    if (!fullAuth) return;
    try {
      // Source-type agnostic — the same endpoint the Authorization Center uses.
      await authorizationCaseService.claim(fullAuth.id);
      await refetchAuthorization();
      toast.success('Case claimed');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to claim the case'));
    }
  }

  const isRequestValid =
    requestForm.reason.trim().length >= 20 &&
    ((requestForm.typeMode === 'catalog' && requestForm.selectedTypeId !== '') ||
      (requestForm.typeMode === 'custom' && requestForm.customTypeName.trim().length > 0));

  async function handleRequest() {
    if (!isRequestValid) {
      if (requestForm.reason.trim().length < 20) {
        toast.error('Reason must be at least 20 characters');
      } else {
        toast.error('Select an approval type or enter a custom name');
      }
      return;
    }
    const dto: {
      reason: string;
      approvalRequestTypeId?: string;
      isCustom?: boolean;
      customApprovalName?: string;
      evidence?: EvidenceSelectionItem[];
    } = {
      reason: requestForm.reason.trim(),
      evidence: requestForm.evidence.length > 0 ? requestForm.evidence : undefined,
    };

    if (requestForm.typeMode === 'catalog') {
      dto.approvalRequestTypeId = requestForm.selectedTypeId;
    } else {
      dto.isCustom = true;
      dto.customApprovalName = requestForm.customTypeName.trim();
    }

    try {
      await requestMutation.mutateAsync({ stepExecutionId, dto });
      setRequestForm(initialRequestForm());
      setShowRequestForm(false);
      toast.success('Authorization request submitted');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to submit authorization request'));
    }
  }

  async function handleRespond(action: 'APPROVED' | 'REJECTED' | 'RETURNED') {
    if (!authorization) return;
    try {
      await respondMutation.mutateAsync({
        stepExecutionId,
        authorizationId: authorization.id,
        dto: { action, comment: respondComment.trim() || undefined },
      });
      setRespondComment('');
      toast.success(
        action === 'APPROVED'
          ? 'Authorization approved'
          : action === 'REJECTED'
            ? 'Authorization rejected'
            : 'Returned to engineer for revision',
      );
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to respond to authorization'));
    }
  }

  async function handleResubmit() {
    if (!authorization) return;
    if (resubmitReason.trim() && resubmitReason.trim().length < 20) {
      toast.error('Updated reason must be at least 20 characters');
      return;
    }
    try {
      await resubmitMutation.mutateAsync({
        stepExecutionId,
        authorizationId: authorization.id,
        dto: { updatedReason: resubmitReason.trim() || undefined },
      });
      setResubmitReason('');
      setShowResubmitForm(false);
      toast.success('Authorization resubmitted for OPS review');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to resubmit authorization'));
    }
  }

  const workflowStage = fullAuth?.workflowStage ?? null;
  const status = authorization?.status ?? null;
  const responding = respondMutation.isPending;
  const resubmitting = resubmitMutation.isPending;
  const requesting = requestMutation.isPending;
  const types = approvalTypes ?? [];

  // ── No authorization raised yet ───────────────────────────────────────────

  if (!authorization) {
    return (
      <div className="space-y-2 rounded-lg border border-amber-300/40 bg-amber-500/5 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faShieldHalved} className="h-3.5 w-3.5 shrink-0 text-amber-600" />
          <p className="text-xs font-medium text-amber-700">Authorization Required</p>
        </div>
        <p className="text-xs text-amber-600/80">
          This step requires an authorization before it can be completed.
        </p>

        {canExecute && !showRequestForm && (
          <button
            type="button"
            onClick={() => setShowRequestForm(true)}
            className="mt-1 flex h-8 items-center gap-1.5 rounded-md border border-amber-400/60 bg-amber-50 px-3 text-xs font-medium text-amber-700 transition hover:bg-amber-100 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faShieldHalved} className="h-3 w-3" />
            Request Authorization
          </button>
        )}

        {canExecute && showRequestForm && (
          <div className="space-y-2 pt-1">
            <TypeSelector
              typeMode={requestForm.typeMode}
              selectedTypeId={requestForm.selectedTypeId}
              customTypeName={requestForm.customTypeName}
              approvalTypes={types}
              onChange={patchRequest}
              disabled={requesting}
            />
            <div>
              <label className="text-xs font-medium text-foreground">
                Reason <span className="text-destructive">*</span>
                <span className="ml-1 font-normal text-muted-foreground">(min 20 chars)</span>
              </label>
              <textarea
                value={requestForm.reason}
                onChange={(e) => patchRequest({ reason: e.target.value })}
                placeholder="Describe why authorization is required for this step…"
                rows={3}
                maxLength={2000}
                disabled={requesting}
                className="mt-1 w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
            </div>
            <EvidenceSelectionPicker
              inspectionId={inspectionId}
              selectedItems={requestForm.evidence}
              onChange={(evidence) => patchRequest({ evidence })}
              disabled={requesting}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowRequestForm(false);
                  setRequestForm(initialRequestForm());
                }}
                disabled={requesting}
                className="h-8 rounded-md border border-border px-3 text-xs text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleRequest()}
                disabled={!isRequestValid || requesting}
                className="flex h-8 items-center gap-1.5 rounded-md bg-amber-600 px-3 text-xs font-medium text-white transition hover:bg-amber-700 disabled:opacity-50"
              >
                {requesting && (
                  <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                )}
                Submit Request
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── PENDING_OPS_REVIEW ────────────────────────────────────────────────────

  if (status === 'PENDING' && workflowStage === 'PENDING_OPS_REVIEW') {
    return (
      <div className="space-y-2 rounded-lg border border-amber-300/40 bg-amber-500/5 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5 shrink-0 text-amber-600" />
            <p className="text-xs font-medium text-amber-700">Pending OPS Review</p>
          </div>
          {fullAuth && fullAuth.history.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition"
            >
              <FontAwesomeIcon
                icon={showHistory ? faChevronUp : faChevronDown}
                className="h-2.5 w-2.5"
              />
              {showHistory ? 'Hide' : 'History'}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        ) : fullAuth ? (
          <div className="space-y-1 text-xs text-amber-700/80">
            {(fullAuth.approvalRequestTypeName ?? fullAuth.customApprovalName) && (
              <p className="font-medium">
                {fullAuth.approvalRequestTypeName ?? `${fullAuth.customApprovalName} (Custom)`}
              </p>
            )}
            <p>
              Requested by <span className="font-medium">{fullAuth.requestedByName}</span>
            </p>
            <p className="text-amber-600/70 italic">"{fullAuth.reason}"</p>
          </div>
        ) : null}

        {showHistory && fullAuth && fullAuth.history.length > 0 && (
          <div className="border-t border-amber-300/30 pt-2 space-y-1">
            {fullAuth.history.map((h) => (
              <div key={h.id} className="flex items-start gap-2 text-xs">
                <span className="mt-0.5 rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">
                  {ACTION_LABEL[h.action] ?? h.action}
                </span>
                <span className="text-muted-foreground">
                  {h.actorName} ({h.actorRoleName}) · {fmtDateTime(h.createdAt)}
                  {h.comment && <span className="ml-1 italic">"{h.comment}"</span>}
                </span>
              </div>
            ))}
          </div>
        )}

        {canRespond && (
          <div className="space-y-2 border-t border-amber-300/30 pt-2">
            <label className="text-xs font-medium text-foreground">
              Response notes <span className="font-normal text-muted-foreground">(optional)</span>
            </label>
            <textarea
              value={respondComment}
              onChange={(e) => setRespondComment(e.target.value)}
              placeholder="Add a note or reason for your decision…"
              rows={2}
              maxLength={2000}
              disabled={responding}
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void handleRespond('APPROVED')}
                disabled={responding}
                className="flex h-8 items-center gap-1.5 rounded-md bg-green-600 px-3 text-xs font-medium text-white transition hover:bg-green-700 disabled:opacity-50"
              >
                {responding ? (
                  <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                )}
                Approve
              </button>
              <button
                type="button"
                onClick={() => void handleRespond('REJECTED')}
                disabled={responding}
                className="flex h-8 items-center gap-1.5 rounded-md bg-red-600 px-3 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50"
              >
                {responding ? (
                  <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                ) : (
                  <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                )}
                Reject
              </button>
              {canReturnToEngineer && (
                <button
                  type="button"
                  onClick={() => void handleRespond('RETURNED')}
                  disabled={responding}
                  className="flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-xs text-foreground transition hover:bg-muted disabled:opacity-50"
                >
                  {responding ? (
                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                  ) : (
                    <FontAwesomeIcon icon={faRotateLeft} className="h-3 w-3" />
                  )}
                  Return to Engineer
                </button>
              )}
            </div>
          </div>
        )}

        {canClaim && (
          <div className="space-y-2 border-t border-amber-300/30 pt-2">
            <p className="text-xs text-muted-foreground">
              This case is unclaimed and sitting in your team's intake pool. Claim it to act on it.
            </p>
            <button
              type="button"
              onClick={() => void handleClaim()}
              className="flex h-8 items-center gap-1.5 rounded-md bg-amber-600 px-3 text-xs font-medium text-white transition hover:bg-amber-700"
            >
              <FontAwesomeIcon icon={faHandPointer} className="h-3 w-3" />
              Claim
            </button>
          </div>
        )}

        {!isLoading && !canRespond && !canClaim && (
          <p className="text-xs text-amber-600/70 italic">
            {hasRespondPermission && isCurrentOwner && ownerUsesAssignment && !isUnclaimed
              ? 'Claimed by another team member.'
              : 'Waiting for OPS / Admin review…'}
          </p>
        )}
      </div>
    );
  }

  // ── RETURNED_TO_ENGINEER ──────────────────────────────────────────────────

  if (status === 'PENDING' && workflowStage === 'RETURNED_TO_ENGINEER') {
    return (
      <div className="space-y-2 rounded-lg border border-orange-300/40 bg-orange-500/5 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faRotateLeft} className="h-3.5 w-3.5 shrink-0 text-orange-600" />
            <p className="text-xs font-medium text-orange-700">Returned to Engineer</p>
          </div>
          {fullAuth && fullAuth.history.length > 0 && (
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition"
            >
              <FontAwesomeIcon
                icon={showHistory ? faChevronUp : faChevronDown}
                className="h-2.5 w-2.5"
              />
              {showHistory ? 'Hide' : 'History'}
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
            Loading…
          </div>
        ) : fullAuth ? (
          <div className="space-y-1 text-xs text-orange-700/80">
            <p>
              Returned by <span className="font-medium">{fullAuth.respondedByName ?? 'OPS'}</span>
            </p>
            {fullAuth.responseNotes && (
              <p className="text-orange-600/70 italic">"{fullAuth.responseNotes}"</p>
            )}
          </div>
        ) : null}

        {showHistory && fullAuth && fullAuth.history.length > 0 && (
          <div className="border-t border-orange-300/30 pt-2 space-y-1">
            {fullAuth.history.map((h) => (
              <div key={h.id} className="flex items-start gap-2 text-xs">
                <span className="mt-0.5 rounded bg-orange-100 px-1.5 py-0.5 font-medium text-orange-800">
                  {ACTION_LABEL[h.action] ?? h.action}
                </span>
                <span className="text-muted-foreground">
                  {h.actorName} ({h.actorRoleName}) · {fmtDateTime(h.createdAt)}
                  {h.comment && <span className="ml-1 italic">"{h.comment}"</span>}
                </span>
              </div>
            ))}
          </div>
        )}

        {canResubmit && !showResubmitForm && (
          <button
            type="button"
            onClick={() => setShowResubmitForm(true)}
            className="flex h-8 items-center gap-1.5 rounded-md border border-orange-400/60 bg-orange-50 px-3 text-xs font-medium text-orange-700 transition hover:bg-orange-100"
          >
            <FontAwesomeIcon icon={faArrowRotateRight} className="h-3 w-3" />
            Resubmit for OPS Review
          </button>
        )}

        {canResubmit && showResubmitForm && (
          <div className="space-y-2 border-t border-orange-300/30 pt-2">
            <label className="text-xs font-medium text-foreground">
              Updated reason{' '}
              <span className="font-normal text-muted-foreground">
                (optional — leave blank to keep original)
              </span>
            </label>
            <textarea
              value={resubmitReason}
              onChange={(e) => setResubmitReason(e.target.value)}
              placeholder="Update your reason if needed (min 20 chars if provided)…"
              rows={3}
              maxLength={2000}
              disabled={resubmitting}
              className="w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowResubmitForm(false);
                  setResubmitReason('');
                }}
                disabled={resubmitting}
                className="h-8 rounded-md border border-border px-3 text-xs text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleResubmit()}
                disabled={resubmitting}
                className="flex h-8 items-center gap-1.5 rounded-md bg-orange-600 px-3 text-xs font-medium text-white transition hover:bg-orange-700 disabled:opacity-50"
              >
                {resubmitting && (
                  <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                )}
                Resubmit
              </button>
            </div>
          </div>
        )}

        {!canResubmit && (
          <p className="text-xs text-orange-600/70 italic">Waiting for engineer to resubmit…</p>
        )}
      </div>
    );
  }

  // ── PENDING (loading / fallback — workflowStage not yet loaded) ───────────

  if (status === 'PENDING') {
    return (
      <div className="space-y-2 rounded-lg border border-amber-300/40 bg-amber-500/5 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faClock} className="h-3.5 w-3.5 shrink-0 text-amber-600" />
          <p className="text-xs font-medium text-amber-700">
            {isLoading ? 'Loading…' : (STAGE_LABEL[workflowStage ?? ''] ?? 'Authorization Pending')}
          </p>
        </div>
      </div>
    );
  }

  // ── APPROVED ──────────────────────────────────────────────────────────────

  if (status === 'APPROVED') {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-green-300/40 bg-green-500/5 px-3 py-2.5">
        <FontAwesomeIcon
          icon={faCircleCheck}
          className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600"
        />
        <div>
          <p className="text-xs font-medium text-green-700">Authorization Approved</p>
          {fullAuth?.respondedByName && (
            <p className="text-xs text-green-600/80">by {fullAuth.respondedByName}</p>
          )}
        </div>
      </div>
    );
  }

  // ── REJECTED ──────────────────────────────────────────────────────────────

  if (status === 'REJECTED') {
    return (
      <div className="space-y-1.5 rounded-lg border border-red-300/40 bg-red-500/5 px-3 py-2.5">
        <div className="flex items-start gap-2">
          <FontAwesomeIcon
            icon={faCircleXmark}
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600"
          />
          <div>
            <p className="text-xs font-medium text-red-700">
              Authorization Rejected — completion not blocked
            </p>
            {fullAuth?.respondedByName && (
              <p className="text-xs text-red-600/80">by {fullAuth.respondedByName}</p>
            )}
            {fullAuth?.responseNotes && (
              <p className="text-xs text-red-600/70 italic mt-0.5">"{fullAuth.responseNotes}"</p>
            )}
          </div>
        </div>
        {canExecute && !showRequestForm && (
          <button
            type="button"
            onClick={() => setShowRequestForm(true)}
            className="flex h-7 items-center gap-1.5 rounded-md border border-red-300/60 bg-red-50 px-2.5 text-xs text-red-700 transition hover:bg-red-100"
          >
            <FontAwesomeIcon icon={faShieldHalved} className="h-3 w-3" />
            Request Again
          </button>
        )}
        {canExecute && showRequestForm && (
          <div className="space-y-2 pt-1 border-t border-red-300/30">
            <TypeSelector
              typeMode={requestForm.typeMode}
              selectedTypeId={requestForm.selectedTypeId}
              customTypeName={requestForm.customTypeName}
              approvalTypes={types}
              onChange={patchRequest}
              disabled={requesting}
            />
            <div>
              <label className="text-xs font-medium text-foreground">
                Reason <span className="text-destructive">*</span>
                <span className="ml-1 font-normal text-muted-foreground">(min 20 chars)</span>
              </label>
              <textarea
                value={requestForm.reason}
                onChange={(e) => patchRequest({ reason: e.target.value })}
                placeholder="Describe why authorization is required…"
                rows={3}
                maxLength={2000}
                disabled={requesting}
                className="mt-1 w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
            </div>
            <EvidenceSelectionPicker
              inspectionId={inspectionId}
              selectedItems={requestForm.evidence}
              onChange={(evidence) => patchRequest({ evidence })}
              disabled={requesting}
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowRequestForm(false);
                  setRequestForm(initialRequestForm());
                }}
                disabled={requesting}
                className="h-8 rounded-md border border-border px-3 text-xs text-foreground transition hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleRequest()}
                disabled={!isRequestValid || requesting}
                className="flex h-8 items-center gap-1.5 rounded-md bg-amber-600 px-3 text-xs font-medium text-white transition hover:bg-amber-700 disabled:opacity-50"
              >
                {requesting && (
                  <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" />
                )}
                Submit Request
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}
